import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';

import {
  DayOfWeek,
  Language,
  MemberContactMethodType,
  MemberContactTimeType,
} from '@vestahealthcare/common/enums';
import { Enum } from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';
import {
  BaseEnum,
  CareTeamMember,
  CareTeamMemberType,
  CareTeamPersonExtended,
  Employee,
  PaginationType,
} from '@vestahealthcare/common/models';
import { LS_ADMIN_CARE_TEAM_FILTERS_V2 } from '@vestahealthcare/common/utils/constants';

import { FilterItem, IconButton, Panel, Tabs } from 'styleguide-v2';
import { TabsItem } from 'styleguide-v2/src/components/Tabs';

import { showGlobalError } from 'dash/src/components/GlobalMessage';
import { CareTeamQuickAddModal } from 'dash/src/pages/CareTeam/modals/CareTeamQuickAdd';
import { CacheServices, fetchSupportedLanguages } from 'dash/src/services';
import {
  GetCareTeamPersonsParams,
  fetchCareTeamPeople,
  quickAddCareTeam,
} from 'dash/src/services/CareTeamServices';
import Session from 'dash/src/services/SessionServices';
import {
  getServerFilters,
  loadNewFiltersFromStorage,
  saveNewFiltersToStorage,
} from 'dash/src/utils/filterUtils';

import { CareTeamFiltersBar } from './CareTeamFiltersBar';
import {
  CARE_TEAM_PAID_OPTIONS,
  CareTeamFiltersModal,
} from './CareTeamFiltersModal';
import { CareTeamPersonTable } from './CareTeamPersonTable';

const CARE_TEAM_TAB_ALL = 'care-team-tab-all';
const CARE_TEAM_TAB_ASSIGNED_TO_ME = 'care-team-tab-assigned';
const DEFAULT_TABS = [
  { value: CARE_TEAM_TAB_ALL, label: translate('careTeam.all') },
  {
    value: CARE_TEAM_TAB_ASSIGNED_TO_ME,
    label: translate('careTeam.assignedToMe'),
  },
];

const PAGE_SIZE = 10;

type KeyGetCareTeamParams = keyof GetCareTeamPersonsParams;

type FiltersState = {
  [x in KeyGetCareTeamParams]?: FilterItem;
};

export const CareTeamDashboard = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>(CARE_TEAM_TAB_ALL);
  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filters, setFilters] = useState<FiltersState>();

  const [tabs, setTabs] = useState<TabsItem[]>(DEFAULT_TABS);
  const [countAll, setCountAll] = useState<number>();
  const [countAssign, setCountAssign] = useState<number>();

  const [languages, setLanguages] = useState<Language[]>([]);
  const [statuses, setStatuses] = useState<BaseEnum[]>([]);
  const [roles, setRoles] = useState<CareTeamMemberType[]>([]);
  const [engagementOwners, setEngagementOwners] = useState<Employee[]>([]);

  const [openAddModal, setOpenAddModal] = useState<boolean>(false);
  const [careTeamPersons, setCareTeamPersons] = useState<
    CareTeamPersonExtended[]
  >([]);
  const [pagination, setPagination] = useState<PaginationType>();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);

  const getInitialData = async () => {
    setLoadingFilters(true);
    const [l, roles, statuses, eo] = await Promise.all([
      await fetchSupportedLanguages(),
      CacheServices.getCareTeamMemberTypes(),
      CacheServices.getCareTeamPersonStatuses(),
      CacheServices.getEngagementOwners(),
    ]);

    setLanguages(l);
    setRoles(roles);
    setStatuses(statuses);
    setEngagementOwners(eo);
    setLoadingFilters(false);
  };

  const prepareFiltersPayload = (
    filters: FiltersState,
  ): GetCareTeamPersonsParams => {
    const serverFilters = getServerFilters(filters);

    return {
      ...serverFilters,
      language: serverFilters.language?.map((item: Language) => ({
        value: item,
      })),
      contactMethods: serverFilters.contactMethods?.map(
        (item: MemberContactMethodType) => ({
          value: item,
        }),
      ),
      preferredDays: serverFilters.preferredDays?.map((item: DayOfWeek) => ({
        value: item,
      })),
      preferredTime: serverFilters.preferredTime?.map(
        (item: MemberContactTimeType) => ({
          value: item,
        }),
      ),
    };
  };

  const getTabFilters = (tab?: string): GetCareTeamPersonsParams => {
    const formattedFilters = prepareFiltersPayload(filters || {});
    if ((tab || selectedTab) === CARE_TEAM_TAB_ASSIGNED_TO_ME) {
      return {
        ...formattedFilters,
        engagementOwnerId: [Session.actingUser.id],
      };
    }
    return formattedFilters;
  };

  const getData = async () => {
    if ((page !== 0 && page * pageSize < careTeamPersons?.length) || !filters)
      return;

    setLoading(true);
    try {
      const { items, pagination } = await fetchCareTeamPeople({
        extended: true,
        offset: page * pageSize,
        limit: pageSize,
        ...getTabFilters(),
      });
      if (page === 0) {
        setCareTeamPersons(items as CareTeamPersonExtended[]);
      } else {
        setCareTeamPersons([
          ...careTeamPersons,
          ...items,
        ] as CareTeamPersonExtended[]);
      }
      setPagination(pagination);
    } catch (e) {
      showGlobalError(e as string);
    }
    setLoading(false);
  };

  const getCountData = async (tab = CARE_TEAM_TAB_ALL) => {
    if (!filters) return;
    const { pagination } = await fetchCareTeamPeople({
      count: true,
      offset: page * pageSize,
      limit: pageSize,
      ...getTabFilters(tab),
    });

    if (tab === CARE_TEAM_TAB_ASSIGNED_TO_ME) {
      setCountAssign(pagination.total);
    } else {
      setCountAll(pagination.total);
    }
  };

  const onAddCTP = () => {
    setOpenAddModal(true);
  };

  useEffect(() => {
    getInitialData();
    getCountData(CARE_TEAM_TAB_ASSIGNED_TO_ME);
  }, []);

  useEffect(() => {
    getData();
    getCountData();
  }, [page, pageSize, selectedTab, filters]);

  useEffect(() => {
    if (filters) {
      saveNewFiltersToStorage(LS_ADMIN_CARE_TEAM_FILTERS_V2, filters);
    }
  }, [filters]);

  useEffect(() => {
    if (
      roles.length &&
      statuses.length &&
      engagementOwners.length &&
      languages.length
    ) {
      const storedFilters = loadNewFiltersFromStorage(
        LS_ADMIN_CARE_TEAM_FILTERS_V2,
        {
          id: {
            label: translate('careTeam.common.id'),
            multiple: true,
          },
          fullName: { label: translate('careTeam.common.name') },
          statusId: {
            data: statuses,
            label: translate('careTeam.common.status'),
            multiple: true,
          },
          typeId: {
            data: roles,
            label: translate('careTeam.common.role'),
            multiple: true,
          },
          engagementOwnerId: {
            data: Employee.toSelectable(engagementOwners),
            label: translate('careTeam.common.engagementOwner'),
            multiple: true,
          },
          language: {
            data: Enum.toSelectable(languages),
            label: translate('careTeam.common.language'),
            multiple: true,
          },
          contactMethods: {
            data: MemberContactMethodType.toSelectable(),
            label: translate('careTeam.common.engagementPreference'),
            multiple: true,
          },
          preferredDays: {
            data: Enum.toSelectable(DayOfWeek.asArray),
            label: translate('careTeam.common.preferredDays'),
            multiple: true,
          },
          preferredTime: {
            data: Enum.toSelectable([
              MemberContactTimeType.MORNING,
              MemberContactTimeType.AFTERNOON,
            ]),
            label: translate('careTeam.common.preferredTimes'),
            multiple: true,
          },
          linkedMembers: {
            label: translate('careTeam.common.linkedMembers'),
          },
          paid: {
            data: CARE_TEAM_PAID_OPTIONS,
            label: translate('careTeam.common.paid'),
          },
        },
      );

      setFilters(storedFilters);
    }
  }, [roles, statuses, engagementOwners, languages]);

  useEffect(() => {
    setTabs([
      {
        value: CARE_TEAM_TAB_ALL,
        label: `${translate('careTeam.all')}${
          countAll ? ` (${countAll})` : ''
        }`,
      },
      {
        value: CARE_TEAM_TAB_ASSIGNED_TO_ME,
        label: `${translate('careTeam.assignedToMe')}${
          countAssign ? ` (${countAssign})` : ''
        }`,
      },
    ]);
  }, [countAll, countAssign]);

  return (
    <Panel id="care-team-section">
      <Panel.Heading title={translate('careTeam.title')} filtersV2>
        <Panel.Actions>
          <IconButton onClick={onAddCTP} tooltip={translate('careTeam.add')}>
            <AddIcon fontSize="large" />
          </IconButton>
        </Panel.Actions>
        <Panel.Tabs>
          <Tabs
            disabled={loading}
            items={tabs}
            onChange={(selectedTab) => {
              setPage(0);
              setSelectedTab(selectedTab);
            }}
            value={selectedTab}
          />
        </Panel.Tabs>
        <Panel.FilterBar
          onClearFilters={() => {
            setPage(0);
            setFilters({});
          }}
          onDeleteFilter={(key: string) => {
            setPage(0);
            setFilters({
              ...filters,
              [key]: undefined,
            });
          }}
          onOpenFilters={() => setOpenFilters(!openFilters)}
          chips={filters}
          inputs={
            <CareTeamFiltersBar
              data={{
                statuses,
                roles,
                engagementOwners,
              }}
              loading={loadingFilters}
              onChange={(filters) => {
                setPage(0);

                setFilters(filters);
              }}
              filters={filters}
            />
          }
        />
      </Panel.Heading>
      <Panel.Body loading={loading}>
        <CareTeamFiltersModal
          data={{
            statuses,
            roles,
            engagementOwners,
            languages,
          }}
          filters={filters}
          loadingFilters={loadingFilters}
          onChange={(filters) => {
            setPage(0);

            setFilters(filters);
          }}
          onClose={() => setOpenFilters(false)}
          open={openFilters}
        />
        <CareTeamPersonTable
          careTeamPersons={careTeamPersons}
          onAdd={onAddCTP}
          onEdit={(ctm) => {
            history.push(
              `/admin/care-team/${ctm.id}?firstName=${ctm.firstName}&lastName=${ctm.lastName}`,
            );
          }}
          onChangePage={setPage}
          onChangePageSize={(val) => {
            setPage(0);
            setPageSize(val);
          }}
          pagination={pagination}
        />
        <CareTeamQuickAddModal
          open={openAddModal}
          onClose={() => setOpenAddModal(false)}
          onSubmit={async (params) => {
            let ctm: CareTeamMember | undefined;
            try {
              ctm = await quickAddCareTeam(params);
              await getData();
            } catch (e) {
              showGlobalError(e as string);
            }
            return ctm?.careTeamPerson || false;
          }}
        />
      </Panel.Body>
    </Panel>
  );
};

export default CareTeamDashboard;
